import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import Layout from './components/Layout';
import DetectionPage from './pages/DetectionPage';
import PatternsPage from './pages/PatternsPage';
import EmbeddingsPage from './pages/EmbeddingsPage';
import StatisticsPage from './pages/StatisticsPage';
import DetectionsPage from './pages/DetectionsPage';
import PredictionsPage from './pages/PredictionsPage';
import LoginPage from './pages/LoginPage';
import { authApi } from './services/api';

function App() {
  const [mode, setMode] = React.useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  const theme = React.useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: mode === 'dark' ? '#ff9800' : '#ef6c00' },
      secondary: { main: mode === 'dark' ? '#ffa726' : '#ff9800' },
      background: mode === 'dark'
        ? { default: '#0f0f11', paper: '#151517' }
        : { default: '#fafafa', paper: '#ffffff' },
      text: mode === 'dark'
        ? { primary: 'rgba(255,255,255,0.92)', secondary: 'rgba(255,255,255,0.65)' }
        : { primary: 'rgba(0,0,0,0.87)', secondary: 'rgba(0,0,0,0.6)' },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
      h4: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
        defaultProps: { elevation: 1 },
      },
      MuiCard: {
        defaultProps: { elevation: 2 },
        styleOverrides: { root: { borderRadius: 16 } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backdropFilter: 'saturate(180%) blur(6px)' },
        },
      },
    },
  }), [mode]);

  const toggleMode = React.useCallback(() => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('themeMode', next); } catch {}
      return next;
    });
  }, []);

  function RequireAuth({ children }: { children: JSX.Element }) {
    const location = useLocation();
    const tokens = authApi.getTokens();
    if (!tokens?.accessToken) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return children;
  }
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="*"
            element={
              <Box sx={{ 
                display: 'flex', 
                minHeight: '100vh',
                overflow: { xs: 'hidden', sm: 'visible' },
                '-webkit-overflow-scrolling': { xs: 'touch', sm: 'auto' },
              }}>
                <Layout mode={mode} onToggleTheme={toggleMode}>
                  <Routes>
                    <Route path="/detections" element={<RequireAuth><DetectionsPage /></RequireAuth>} />
                    <Route path="/patterns" element={<RequireAuth><PatternsPage /></RequireAuth>} />
                    <Route path="/embeddings" element={<RequireAuth><EmbeddingsPage /></RequireAuth>} />
                    <Route path="/statistics" element={<RequireAuth><StatisticsPage /></RequireAuth>} />
                    <Route path="*" element={<Navigate to="/statistics" replace />} />
                  </Routes>
                </Layout>
              </Box>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
